/*!--------------------------------------------------------------------------
// <copyright file="GSS.portal.tagging.ts" company="Gallup" product="GSS" owner="All">
//    Copyright (c) The Gallup Organization. All rights reserved.
// </copyright>
//--------------------------------------------------------------------------*/
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var GSS;
(function (GSS) {
    // ---------------------------
    // Declarations of Platform analytics (oculus)
    // ---------------------------
    /**
     * Represents the button event properties.
     */
    GSS.ButtonEventDetail = {
        Label: "Button Click",
        Name: "PortalContainer",
        Path: "gss.portalcontainer.button.click",
    };
    /**
    * Represents the search event properties.
    */
    GSS.SearchEventDetail = {
        Label: "Search",
        Name: "PortalContainer",
        Path: "gss.portalcontainer.search",
    };
    /**
    * Represents the menu click event properties.
    */
    GSS.MenuEventDetail = {
        Label: 'Menu Click',
        Name: 'PortalContainer',
        Path: 'gss.portalcontainer.menuitem.click',
    };
    /**
     * Represents different event types.
     */
    GSS.EventTypes = {
        LinkClick: "link_click",
        ButtonClick: "button_click",
        Search: "search",
    };
    var DispatchTarget;
    (function (DispatchTarget) {
        DispatchTarget[DispatchTarget["CONSOLE"] = 0] = "CONSOLE";
        DispatchTarget[DispatchTarget["GSS"] = 1] = "GSS";
        DispatchTarget[DispatchTarget["SERVER"] = 2] = "SERVER";
    })(DispatchTarget || (DispatchTarget = {}));
    // endpoint url to track events from uauthentcated users
    var _trackUnauthenticatedUsersUrl = '../_Help/TrackUnauthenticatedUsers';
    var isPlatFormAnalyticsInitialised;
    // Custom dimension mapping to match configuration in the Google Analytics property
    var DIMENSION_MAP = {
        dimension3: "viewedLang",
        dimension4: "browserLang",
        dimension9: "clientID",
        dimension10: "userCountry",
        dimension11: "isGallupUser",
        dimension12: "isImpersonating",
    };
    // bool, stores if the user has consented to marketing according to the cookie banner
    var ALLOW_MARKETING = GSS.disableCookieBanner ? false : gcb && gcb.check("m");
    // bool, stores if the user has consented to analytics according to the cookie banner
    var allowAnalytics = GSS.disableCookieBanner ? false : gcb && gcb.check("a");
    var DEFAULT_CONFIG = {
        anonymize_ip: true,
        allow_google_signals: ALLOW_MARKETING,
        allow_ad_personalization_signals: ALLOW_MARKETING,
        send_page_view: false,
        custom_map: DIMENSION_MAP,
        cookie_flags: "secure",
    };
    // Prepare user data to send to Google. Keys match the values in the DIMENSION_MAP above, except user_id which is a standard Google key.
    var userDimensions = {
        user_id: GSS.gaUserId, // this is a Google-level dimension that allows more accurate multi-device tracking for known users
        viewedLang: GSS.gaViewedLang, // use lower case to ensure consistency across sites
        browserLang: GSS.gaBrowserLang, // the browser's preferred language whether it is the same as the chosen language or not
        clientID: GSS.gaGallupClientId,
        userCountry: GSS.gaUserCountry,
        isGallupUser: GSS.gaIsGallupUser,
        isImpersonating: GSS.gaIsImpersonating,
    };
    var tagging;
    (function (tagging) {
        tagging.tagNames = {
            StrengthsSurveyTransferTile: 'StrengthsSurveyTransferTile',
            StrengthSurveyTileComplete: 'StrengthSurveyTileComplete',
            StrengthSurveyTile: 'StrengthSurveyTile',
            StrengthSurveyCopy: 'StrengthSurveyCopy',
            StrengthSurvey: 'StrengthSurvey'
        };
        function tag(app) {
            // send events for optimize 
            if (typeof window.gtag === "function") {
                window.gtag("event", "optimizely." + app);
            }
        }
        tagging.tag = tag;
        ;
    })(tagging = GSS.tagging || (GSS.tagging = {}));
    var analytics;
    (function (analytics) {
        analytics.attributeNames = {
            action: 'data-ga-action',
            category: 'data-ga-category',
            createSurveyAction: 'data-gss-createsurvey-action',
            event: 'data-ga-event',
            label: 'data-ga-label',
            linkAction: 'data-ga-link-action',
            linkLabel: 'data-ga-link-label',
            mobileAppAdLabel: 'data-ga-mobile-app-ad-label',
        };
        analytics.attributeValues = {
            createNewProjectModal: 'createNewProjectModal',
            footerLinkClick: 'data-ga-footer-link-click',
            helpCenterCategoryClick: 'help-center-category-click',
            helpCenterLinkClick: 'help-center-link-click',
            helpCenterCallSupportLinkClick: 'help-center-call-support-link-click',
            helpCenterContactSupportLinkClick: 'help-center-contact-support-link-click',
            helpCenterContactUsEmailSubmit: 'help-center-contact-us-email-submit',
            helpCenterPhoneNumberClick: 'help-center-phone-number-click',
            helpCenterPopularTopicsClick: 'help-center-popular-topics-click',
            helpCenterSubcategoryClick: 'help-center-subcategory-click',
            helpCenterWatchButtonClick: 'help-center-watch-button-click',
            helpCenterChatTileClick: 'help-center-chat-tile-click',
        };
        analytics.eventCategories = {
            clientMenu: "client_menu",
            contactUs: 'contact_us',
            copyLink: 'copy_link',
            footer: "footer",
            languageMenu: "language_menu",
            logoClick: "logo_click",
            mainCategories: 'main_categories',
            mobileAppAd: "mobile_app_ad",
            moreFromGallupAdBooks: "more_from_gallup_ad_books",
            moreFromGallupAdCliftonstrengths: "more_from_gallup_ad_cliftonstrengths",
            moreFromGallupAdCourses: "more_from_gallup_ad_courses",
            myAccountMenu: "my_account_menu",
            notificationBell: "notification_bell",
            popularTopics: 'popular_topics',
            portalMenu: "portal_menu",
            shoppingCart: "shopping_cart",
            search: "search",
            topActionSurveyInvitation: "top_action_survey_invitation",
            yourSurveys: "your_surveys",
        };
        analytics.eventActions = {
            bellClick: "bell_click",
            buttonClick: "button_click",
            categoryLinkClick: "category_link_click",
            click: "click",
            contactLinkClick: "contact_link_click",
            formSubmit: 'form_submit',
            gallupAccessLogo: "gallup_access_logo",
            imageClick: "image_click",
            linkClick: "link_click",
            menuClick: "menu_click",
            menuToggle: "menu_toggle",
            modalOpen: 'modal_open',
            phoneLinkClick: 'phone_link_click',
            search: "search",
            searchClick: "search_click",
            youtubeLinkClick: 'youtube_link_click',
        };
        analytics.eventLabels = {
            appStore: "App Store",
            buyCodes: "Buy Codes",
            createSurvey: "Create Survey",
            dashboard: "Dashboard",
            email: 'Email',
            googlePlay: "Google Play",
            search: "Search",
            shopCliftonStrengthsBooks: "Shop CliftonStrengths Books",
            subscribe: "Subscribe",
            viewAllCourses: "View All Courses",
        };
        // bool, stores if the user has consented to analytics according to the cookie banner
        var allowAnalytics = GSS.disableCookieBanner ? false : gcb && gcb.check("a");
        /**
         * @description Function to handle analytics data on client change.
         * */
        function changeClient(el) {
            var _newClientId = $(el).closest('[data-ga-clientid]').attr('data-ga-clientid');
            if (_newClientId && _newClientId !== '') {
                var _action = "Change from ".concat(GSS.gaGallupClientId);
                var _data = {
                    event_category: GSS.analytics.eventCategories.clientMenu,
                    event_label: "Change to ".concat(_newClientId)
                };
                GSS.analytics.event(_action, _data);
            }
        }
        analytics.changeClient = changeClient;
        /**
         * @description Function called to send page view event data.
         * @param {string} title Page title for this route in English, e.g. 'Survey Dashboard', 'Edit Team Details'.
         * @param {string} route The partial url for this route, e.g. 'dashboard', 'team/edit-details/1234567'.
         * @param {Function} callback Called when the event is sent (optional).
         * @param {string | string[]} send_to If not specified, page view goes to all properties (optional).
         * @param {string} applicationName ApplicationName for the respective page view event (optional).
        */
        function pageView(title, route, callback, send_to) {
            //send data to oculus
            if (route != undefined && route.trim() !== "" && GSS.isAuthenticated) {
                sendPageViewEventDataToOculus(route);
            }
            else if (route != undefined && route.trim() !== "" && !GSS.isAuthenticated) {
                // tracking unauthenticated users pageview for platform analtyics.
                var _pageViewCommandOptions = createPageViewCommandOptions(route);
                if (!_pageViewCommandOptions.eventDetails.url) {
                    // this is same way oculus is adding the url if not present, have to manually add it here because for unauthenticated users, it is sent as SQS message to GAR.
                    _pageViewCommandOptions.eventDetails.url = window.location.pathname + window.location.search + window.location.hash;
                }
                _pageViewCommandOptions.eventDetails.url = encodeURIComponent(_pageViewCommandOptions.eventDetails.url);
                var _extendedPlatformAnalyticsPayload = __assign(__assign({}, _pageViewCommandOptions), { page_location: window.location.href, platform: {
                        locale: userDimensions.viewedLang,
                        resolution: {
                            height: window.screen.height,
                            width: window.screen.width,
                        },
                        type: 'desktop',
                        userAgent: window.navigator.userAgent,
                    } });
                GSS.PortalContainer.Request.postByEvent(this, _trackUnauthenticatedUsersUrl, _extendedPlatformAnalyticsPayload, GSS.common.returnType.Json, false, false, undefined, 'application/json');
            }
            if (!GSS.isAuthenticated) {
                recordPageViewToGoogleAnalytics(title, route, callback, send_to);
            }
        }
        analytics.pageView = pageView;
        /**
         * @description Function called to send event data.
         * @param {string} action Ideally snake case to match the google analytics standard.
         * @param {Record<string, string | string[] | number | Boolean | Function>} data Object of parameters for the event, should include event_category.
         * @param {boolean} nonInteraction Indicates whether non-interaction event or not (optional).
         * @param {platformAnalyticsDataGar} platformAnalyticsData Platform analytics event data for oculus (optional).
         * @param {EventType} eventType EventType like link_click, button_click etc.
         */
        function event(action, data, platformAnalyticsData, eventType) {
            if (eventType === void 0) { eventType = GSS.EventTypes.LinkClick; }
            if (platformAnalyticsData && GSS.isAuthenticated) {
                //initialise GAR platform analytics if not initialised
                GSS.analytics.initializePlatformAnalytics();
                switch (eventType) {
                    case GSS.EventTypes.LinkClick: {
                        sendMenuClickEventDataToOculus(platformAnalyticsData);
                        break;
                    }
                    case GSS.EventTypes.ButtonClick: {
                        var _eventDetail = { label: GSS.ButtonEventDetail.Label, name: GSS.ButtonEventDetail.Name, path: GSS.ButtonEventDetail.Path, type: eventType };
                        sendClickEventDataToOculus(_eventDetail, platformAnalyticsData);
                        break;
                    }
                    case GSS.EventTypes.Search: {
                        var _eventDetail = { label: GSS.SearchEventDetail.Label, name: GSS.SearchEventDetail.Name, path: GSS.SearchEventDetail.Path, type: eventType };
                        sendClickEventDataToOculus(_eventDetail, platformAnalyticsData);
                        break;
                    }
                    default: {
                        sendMenuClickEventDataToOculus(platformAnalyticsData);
                        break;
                    }
                }
            }
            else if (platformAnalyticsData && !GSS.isAuthenticated) {
                // tracking unauthenticated users events for platform analtyics.
                switch (eventType) {
                    case GSS.EventTypes.LinkClick: {
                        var _platformAnalyticsPayload = createInteractionCommandOptionsForMenuClickEventEvent(platformAnalyticsData);
                        if (!_platformAnalyticsPayload.eventDetails.url) {
                            // this is same way oculus is adding the url if not present, have to manually add it here because for unauthenticated users, it is sent as SQS message to GAR.
                            _platformAnalyticsPayload.eventDetails.url = window.location.pathname + window.location.search + window.location.hash;
                        }
                        _platformAnalyticsPayload.eventDetails.url = encodeURIComponent(_platformAnalyticsPayload.eventDetails.url);
                        var _extendedPlatformAnalyticsPayload = __assign(__assign({}, _platformAnalyticsPayload), { host: window.location.host, platform: {
                                locale: userDimensions.viewedLang,
                                resolution: {
                                    height: window.screen.height,
                                    width: window.screen.width,
                                },
                                type: 'desktop',
                                userAgent: window.navigator.userAgent,
                            } });
                        GSS.PortalContainer.Request.postByEvent(this, _trackUnauthenticatedUsersUrl, _extendedPlatformAnalyticsPayload, GSS.common.returnType.Json, false, false, undefined, 'application/json');
                        break;
                    }
                    case GSS.EventTypes.ButtonClick: {
                        var _eventDetail = { label: GSS.ButtonEventDetail.Label, name: GSS.ButtonEventDetail.Name, path: GSS.ButtonEventDetail.Path, type: eventType };
                        var _platformAnalyticsPayload = createInteractionCommandOptionsForClickEvents(_eventDetail, platformAnalyticsData);
                        if (!_platformAnalyticsPayload.eventDetails.url) {
                            // this is same way oculus is adding the url if not present, have to manually add it here because for unauthenticated users, it is sent as SQS message to GAR.
                            _platformAnalyticsPayload.eventDetails.url = window.location.pathname + window.location.search + window.location.hash;
                        }
                        _platformAnalyticsPayload.eventDetails.url = encodeURIComponent(_platformAnalyticsPayload.eventDetails.url);
                        var _extendedPlatformAnalyticsPayload = __assign(__assign({}, _platformAnalyticsPayload), { host: window.location.host, platform: {
                                locale: userDimensions.viewedLang,
                                resolution: {
                                    height: window.screen.height,
                                    width: window.screen.width,
                                },
                                type: 'desktop',
                                userAgent: window.navigator.userAgent,
                            } });
                        GSS.PortalContainer.Request.postByEvent(this, _trackUnauthenticatedUsersUrl, _extendedPlatformAnalyticsPayload, GSS.common.returnType.Json, false, false, undefined, 'application/json');
                        break;
                    }
                    case GSS.EventTypes.Search: {
                        var _eventDetail = { label: GSS.SearchEventDetail.Label, name: GSS.SearchEventDetail.Name, path: GSS.SearchEventDetail.Path, type: eventType };
                        var _platformAnalyticsPayload = createInteractionCommandOptionsForClickEvents(_eventDetail, platformAnalyticsData);
                        if (!_platformAnalyticsPayload.eventDetails.url) {
                            // this is same way oculus is adding the url if not present, have to manually add it here because for unauthenticated users, it is sent as SQS message to GAR.
                            _platformAnalyticsPayload.eventDetails.url = window.location.pathname + window.location.search + window.location.hash;
                        }
                        _platformAnalyticsPayload.eventDetails.url = encodeURIComponent(_platformAnalyticsPayload.eventDetails.url);
                        var _extendedPlatformAnalyticsPayload = __assign(__assign({}, _platformAnalyticsPayload), { host: window.location.host, platform: {
                                locale: userDimensions.viewedLang,
                                resolution: {
                                    height: window.screen.height,
                                    width: window.screen.width,
                                },
                                type: 'desktop',
                                userAgent: window.navigator.userAgent,
                            } });
                        GSS.PortalContainer.Request.postByEvent(this, _trackUnauthenticatedUsersUrl, _extendedPlatformAnalyticsPayload, GSS.common.returnType.Json, false, false, undefined, 'application/json');
                        break;
                    }
                }
            }
            if (!GSS.isAuthenticated || !platformAnalyticsData) {
                recordEventToGoogleAnalytics(action, data);
            }
        }
        analytics.event = event;
        ;
        /**
         * @description Function to record page view for google analytics
         * @param title title of the page.
         * @param route route of the page.
         * @param callback callback function to be called after the event is sent.(optional)
         * @returns
         */
        function recordPageViewToGoogleAnalytics(title, route, callback, send_to) {
            // if the user has denied the use of analytics cookies, do not process the page view.
            if (GSS.disableCookieBanner || !allowAnalytics) {
                return;
            }
            // warn the users if either of the parameters are not provided.
            if (!title || !route) {
                return void 0;
            }
            // sanitize the data
            var pageTitle = title.replace(/\s+/g, " ").trim(); // make all whitespace characters a single regular space character
            // construct event data for Google page view
            var eventData = {
                page_title: "".concat(pageTitle),
                page_path: route,
                page_location: window.location.href,
                hostname: window.location.hostname,
                send_to: send_to || window.defaultSendTo || ["consumer", "client"],
            };
            // attach optional event callback
            if (callback) {
                eventData.event_callback = callback;
            }
            // combine event data with user meta data.
            var combinedData = __assign(__assign({}, (eventData)), userDimensions);
            if (typeof window.gtag === "function") {
                window.gtag("event", "page_view", combinedData);
            }
        }
        analytics.recordPageViewToGoogleAnalytics = recordPageViewToGoogleAnalytics;
        /**
         * @description Function to record event to google analytics.
         * @param eventAction button_click,link_click, etc.
         * @param eventData event category,label,value data
         * @param nonInteraction flag to indicate if the event is non-interaction
         * @returns
         */
        function recordEventToGoogleAnalytics(eventAction, eventData) {
            // if the user has denied the use of analytics cookies, do not process the event.
            if (GSS.disableCookieBanner || !allowAnalytics) {
                return;
            }
            // Check the action parameter
            if (!eventAction) {
                return void 0;
            }
            // dissalow page_view event to ensure consistent data
            if (eventAction === "page_view") {
                return void 0;
            }
            eventData.send_to = ["consumer", "client"];
            //set non_interaction to true if it is truthy, or false otherwise
            eventData.non_interaction = !!eventData.non_interaction;
            // Include the custom dimension data with the event
            var combinedData = __assign(__assign({}, (eventData)), userDimensions);
            if (typeof window.gtag === "function") {
                window.gtag("event", eventAction, combinedData);
            }
        }
        analytics.recordEventToGoogleAnalytics = recordEventToGoogleAnalytics;
        /**
         * @description send portal menu click event deatils to oculus library.
         * @param {platformAnalyticsDataGar} platformAnalyticsDataGar menu click event details
         * */
        function sendMenuClickEventDataToOculus(pAnalyticsData) {
            var _interactionData = createInteractionCommandOptionsForMenuClickEventEvent(pAnalyticsData);
            GSS.oculus.recordInteraction(_interactionData);
        }
        analytics.sendMenuClickEventDataToOculus = sendMenuClickEventDataToOculus;
        /**
        * @description creates interaction commandOptions payload for platform analytics for click events
        * @param {Interaction} eventDetail click event details
        * @param {platformAnalyticsDataGar} platformAnalyticsDataGar analytics object
        * */
        function createInteractionCommandOptionsForMenuClickEventEvent(pAnalyticsData) {
            return {
                eventDetails: __assign({ label: 'Menu Click', name: 'PortalContainer', path: 'gss.portalcontainer.menuitem.click', type: 'link_click' }, (pAnalyticsData.url ? { url: pAnalyticsData.url } : {})),
                state: {
                    customFilterAttributes: {
                        menuItem: [{ id: pAnalyticsData.data_ga_label, desc: pAnalyticsData.data_ga_label }],
                    }
                }
            };
        }
        /**
         * @description send portal click event deatils to oculus library.
         * @param {Interaction} eventDetail click event details
         * @param {platformAnalyticsDataGar} platformAnalyticsDataGar analytics object
         * */
        function sendClickEventDataToOculus(eventDetail, pAnalyticsData) {
            var _interactionData = createInteractionCommandOptionsForClickEvents(eventDetail, pAnalyticsData);
            GSS.oculus.recordInteraction(_interactionData);
        }
        analytics.sendClickEventDataToOculus = sendClickEventDataToOculus;
        /**
         * @description creates interaction commandOptions payload for platform analytics for click events
         * @param {Interaction} eventDetail click event details
         * @param {platformAnalyticsDataGar} platformAnalyticsDataGar analytics object
         * */
        function createInteractionCommandOptionsForClickEvents(eventDetail, pAnalyticsData) {
            return {
                eventDetails: __assign({ label: eventDetail.label, name: eventDetail.name, path: eventDetail.path, type: eventDetail.type }, (pAnalyticsData && pAnalyticsData.url ? { url: pAnalyticsData.url } : {})),
                state: {
                    customFilterAttributes: __assign({ item: pAnalyticsData ? [{ id: pAnalyticsData.data_ga_label, desc: pAnalyticsData.data_ga_label }] : [] }, (pAnalyticsData && pAnalyticsData.searchText ? { searchText: [{ id: pAnalyticsData.searchText, desc: pAnalyticsData.searchText }] } : {}))
                }
            };
        }
        /**
         * @description send portal page view event deatils to oculus library.
         * @param {applicationName} applicationName application name
         * */
        function sendPageViewEventDataToOculus(applicationName) {
            //initialise GAr platform analytics if not initialised
            GSS.analytics.initializePlatformAnalytics();
            var _pageViewCommandOptions = createPageViewCommandOptions(applicationName);
            //record the page view event asynchronously
            GSS.oculus.recordPageView(_pageViewCommandOptions);
        }
        analytics.sendPageViewEventDataToOculus = sendPageViewEventDataToOculus;
        /**
        * @description create page view command options payload for platform analytics.
        * @param {applicationName} applicationName application name
        * */
        function createPageViewCommandOptions(applicationName) {
            var _location = window.location.href;
            return {
                eventDetails: {
                    label: "Application Loaded",
                    name: "PortalContainer",
                    path: "gss.portalcontainer.".concat(applicationName, ".load"),
                    type: "page_view",
                    url: _location,
                },
                state: {
                    customFilterAttributes: {
                        menuItems: [{ id: 'AppName', desc: applicationName }]
                    },
                },
            };
        }
        analytics.createPageViewCommandOptions = createPageViewCommandOptions;
        /**
         * @description Function to handle analytics data on portal menu click.
         * */
        function onPortalMenuClick() {
            var _action = GSS.analytics.eventActions.menuToggle;
            var _data = {
                event_category: GSS.analytics.eventCategories.portalMenu,
                event_label: this.classList.contains('collapsed') ? "Close" : "Open"
            };
            GSS.analytics.event(_action, _data);
        }
        analytics.onPortalMenuClick = onPortalMenuClick;
        /**
         * @description Function to handle analytics data on footer link click.
         * */
        function onFooterLinkClick() {
            var _label = this.getAttribute(GSS.analytics.attributeNames.linkLabel);
            if (_label && _label !== '') {
                var _action = GSS.analytics.eventActions.linkClick;
                var _data = {
                    event_category: GSS.analytics.eventCategories.footer,
                    event_label: _label
                };
                var _platformAnalyticsData = {
                    data_ga_label: _label
                };
                GSS.analytics.event(_action, _data, _platformAnalyticsData);
            }
        }
        analytics.onFooterLinkClick = onFooterLinkClick;
        /**
         * @description Function to handle analytics data on portal menu item click.
         * */
        function onPortalMenuItemClick() {
            var _label = this.getAttribute('data-ga-label');
            if (_label && _label !== '') {
                var _action = GSS.analytics.eventActions.menuClick;
                var _data = {
                    event_category: GSS.analytics.eventCategories.portalMenu,
                    event_label: _label
                };
                var _meunItemhref = this.getAttribute('href');
                var _platformAnalyticsData = __assign({ data_ga_label: _label }, (_meunItemhref ? { url: _meunItemhref } : {}));
                GSS.analytics.event(_action, _data, _platformAnalyticsData);
            }
        }
        analytics.onPortalMenuItemClick = onPortalMenuItemClick;
        /**
         * @description Function to handle analytics data on Gallup Access logo click.
         * */
        function onGallupAccessLogoClick() {
            var _action = GSS.analytics.eventActions.gallupAccessLogo;
            var _data = {
                event_category: GSS.analytics.eventCategories.logoClick,
                event_label: GSS.analytics.eventLabels.dashboard,
            };
            GSS.analytics.event(_action, _data);
        }
        analytics.onGallupAccessLogoClick = onGallupAccessLogoClick;
        /**
         * @description Function to handle analytics data on language selection click.
         * */
        function onLanguageSelectionClick() {
            var _language = this.getAttribute('gss-data-language-selection-dropdown');
            if (_language && _language !== '') {
                var _action = "Change from ".concat(GSS.gaViewedLang);
                var _data = {
                    event_category: GSS.analytics.eventCategories.languageMenu,
                    event_label: "Change to ".concat(_language.toLowerCase()),
                };
                GSS.analytics.event(_action, _data);
            }
        }
        analytics.onLanguageSelectionClick = onLanguageSelectionClick;
        /**
         * @description Function to handle analytics data on notification bell click.
         * */
        function onNotificationBellClick() {
            var _count = this.querySelector('#notificationCount');
            var _action = GSS.analytics.eventActions.bellClick;
            var _data = {
                event_category: GSS.analytics.eventCategories.notificationBell,
                event_label: (_count && _count.getInnerHTML() !== '' ? _count.getInnerHTML() : 0)
            };
            GSS.analytics.event(_action, _data);
        }
        analytics.onNotificationBellClick = onNotificationBellClick;
        /**
         * @description Function to handle analytics data on shopping cart click.
         * */
        function onShoppingCartClick() {
            var _label = this.href;
            if (_label && _label !== '') {
                var _action = GSS.analytics.eventActions.click;
                var _data = {
                    event_category: GSS.analytics.eventCategories.shoppingCart,
                    event_label: encodeURI(_label)
                };
                GSS.analytics.event(_action, _data);
            }
        }
        analytics.onShoppingCartClick = onShoppingCartClick;
        /**
         * @description Function to handle analytics data on My Account menu click.
         * */
        function onMyAccountMenuClick() {
            var _label = this.getAttribute('aria-expanded');
            if (_label && _label !== '') {
                var _action = GSS.analytics.eventActions.menuToggle;
                var _data = {
                    event_category: GSS.analytics.eventCategories.myAccountMenu,
                    event_label: _label === "true" ? "Open" : "Close"
                };
                GSS.analytics.event(_action, _data);
            }
        }
        analytics.onMyAccountMenuClick = onMyAccountMenuClick;
        /**
         * @description Function to handle analytics data on My Account menu item click.
         * */
        function onMyAccountMenuItemClick() {
            var _label = this.getAttribute('data-ga-account-menu-label');
            if (_label && _label !== '') {
                var _action = GSS.analytics.eventActions.menuClick;
                var _data = {
                    event_category: GSS.analytics.eventCategories.myAccountMenu,
                    event_label: _label,
                };
                GSS.analytics.event(_action, _data);
            }
        }
        analytics.onMyAccountMenuItemClick = onMyAccountMenuItemClick;
        /**
         * @description Function to handle analytics data on Top Actions survey invite click.
         * */
        function onTopActionSurveyInviteClick() {
            var _eventAction = this.getAttribute('data-ga-survey-tile-button');
            var _label = this.getAttribute('data-ga-survey-tile-template');
            if (_eventAction && _eventAction !== '' && _label && _label !== '') {
                var _action = _eventAction;
                var _data = {
                    event_category: GSS.analytics.eventCategories.topActionSurveyInvitation,
                    event_label: _label,
                };
                GSS.analytics.event(_action, _data);
            }
        }
        analytics.onTopActionSurveyInviteClick = onTopActionSurveyInviteClick;
        /**
         * @description Function to handle analytics data on Your Surveys button click.
         * */
        function onYourSurveysButtonClick() {
            var _action = GSS.analytics.eventActions.buttonClick;
            var _data = {
                event_category: GSS.analytics.eventCategories.yourSurveys,
                event_label: GSS.analytics.eventLabels.createSurvey
            };
            GSS.analytics.event(_action, _data);
        }
        analytics.onYourSurveysButtonClick = onYourSurveysButtonClick;
        /**
         * @description Function to handle analytics data on More From Gallup ads click.
         * */
        function onMoreFromGallupAdsClick() {
            var _innerHtml = this.getInnerHTML();
            if (_innerHtml) {
                switch (_innerHtml.toLowerCase()) {
                    case GSS.analytics.eventLabels.shopCliftonStrengthsBooks.toLowerCase(): {
                        var _action = GSS.analytics.eventActions.linkClick;
                        var _data = {
                            event_category: GSS.analytics.eventCategories.moreFromGallupAdBooks,
                            event_label: GSS.analytics.eventLabels.shopCliftonStrengthsBooks
                        };
                        GSS.analytics.event(_action, _data);
                        break;
                    }
                    case GSS.analytics.eventLabels.buyCodes.toLowerCase(): {
                        var _action = GSS.analytics.eventActions.buttonClick;
                        var _data = {
                            event_category: GSS.analytics.eventCategories.moreFromGallupAdCliftonstrengths,
                            event_label: GSS.analytics.eventLabels.subscribe
                        };
                        GSS.analytics.event(_action, _data);
                        break;
                    }
                }
            }
        }
        analytics.onMoreFromGallupAdsClick = onMoreFromGallupAdsClick;
        /**
         * @description Function to handle analytics data on More From Gallup Ads courses click.
         * */
        function onMoreFromGallupAdsCoursesClick() {
            var _innerHtml = this.getInnerHTML();
            if (_innerHtml && _innerHtml.toLowerCase() === GSS.analytics.eventLabels.viewAllCourses.toLowerCase()) {
                var _action = GSS.analytics.eventActions.linkClick;
                var _data = {
                    event_category: GSS.analytics.eventCategories.moreFromGallupAdCourses,
                    event_label: GSS.analytics.eventLabels.viewAllCourses
                };
                GSS.analytics.event(_action, _data);
            }
        }
        analytics.onMoreFromGallupAdsCoursesClick = onMoreFromGallupAdsCoursesClick;
        /**
         * @description Function to handle analytics data on mobile app ad click.
         * */
        function onMobileAppAdClick() {
            var _label = this.getAttribute(GSS.analytics.attributeNames.mobileAppAdLabel);
            var _action = GSS.analytics.eventActions.imageClick;
            var _data = {
                event_category: GSS.analytics.eventCategories.mobileAppAd,
                event_label: _label
            };
            GSS.analytics.event(_action, _data);
        }
        analytics.onMobileAppAdClick = onMobileAppAdClick;
        function initializePlatformAnalytics() {
            // if it is not already initialised
            if (!isPlatFormAnalyticsInitialised) {
                isPlatFormAnalyticsInitialised = true;
                var _tenantId = 'GSS';
                var _dispatchTargets = [DispatchTarget['SERVER'], DispatchTarget['CONSOLE'], DispatchTarget['GSS']];
                var _antiforgeryToken = $("input[name='__RequestVerificationToken']:first").val();
                var _startupOptions = {
                    appDetails: {
                        name: 'PortalContainer',
                        tenantId: _tenantId,
                        version: '0',
                    },
                    dispatchURL: '/ui-sl/ext/api/apa/v1/record?gssComponentId=' + GSS.oculusComponentId + '&__RequestVerificationToken=' + _antiforgeryToken,
                    registrationURL: '/ui-sl/ext/api/apa/v1/register?gssComponentId=' + GSS.oculusComponentId + '&__RequestVerificationToken=' + _antiforgeryToken,
                    cacheVersion: null,
                    dispatchTargets: _dispatchTargets,
                    requestHeaders: null,
                };
                GSS.oculus.startUserTracking(_startupOptions);
            }
        }
        analytics.initializePlatformAnalytics = initializePlatformAnalytics;
        var HelpCenter;
        (function (HelpCenter) {
            /**
             * @description Function to handle analytics data on Help Center "Watch Now" button click.
             * */
            function onWatchNowButtonClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _paLabel = this.getAttribute("data-pa-label");
                    var _meunItemhref = this.getAttribute('href');
                    var _action = GSS.analytics.eventActions.youtubeLinkClick;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.copyLink,
                        event_label: _label
                    };
                    var _platformAnalyticsData = __assign({ data_ga_label: _paLabel }, (_meunItemhref ? { url: _meunItemhref } : {}));
                    GSS.analytics.event(_action, _data, _platformAnalyticsData);
                }
            }
            HelpCenter.onWatchNowButtonClick = onWatchNowButtonClick;
            /**
             * @description Function to handle analytics data on Help Center category click.
             * */
            function onCategoryClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.categoryLinkClick;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.mainCategories,
                        event_label: _label
                    };
                    var _meunItemhref = this.getAttribute('href');
                    var _platformAnalyticsData = __assign({ data_ga_label: _label }, (_meunItemhref ? { url: _meunItemhref } : {}));
                    GSS.analytics.event(_action, _data, _platformAnalyticsData);
                }
            }
            HelpCenter.onCategoryClick = onCategoryClick;
            /**
             * @description Function to handle analytics data on Help Center subcategory click.
             * */
            function onSubCategoryClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.categoryLinkClick;
                    var _category = this.getAttribute(GSS.analytics.attributeNames.category);
                    var _data = {
                        event_category: _category,
                        event_label: _label
                    };
                    var _meunItemhref = this.getAttribute('href');
                    var _platformAnalyticsData = __assign({ data_ga_label: _label }, (_meunItemhref ? { url: _meunItemhref } : {}));
                    GSS.analytics.event(_action, _data, _platformAnalyticsData);
                }
            }
            HelpCenter.onSubCategoryClick = onSubCategoryClick;
            /**
             * @description Function to handle analytics data on Help Center link click.
             * */
            function onLinkClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.linkClick;
                    var _category = this.getAttribute(GSS.analytics.attributeNames.category);
                    var _data = {
                        event_category: _category,
                        event_label: _label
                    };
                    var _meunItemhref = this.getAttribute('href');
                    var _platformAnalyticsData = __assign({ data_ga_label: _label }, (_meunItemhref ? { url: _meunItemhref } : {}));
                    GSS.analytics.event(_action, _data, _platformAnalyticsData);
                }
            }
            HelpCenter.onLinkClick = onLinkClick;
            /**
             * @description Function to handle analytics data on Help Center popular topics click.
             * */
            function onPopularTopicsClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.linkClick;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.popularTopics,
                        event_label: _label
                    };
                    GSS.analytics.event(_action, _data);
                }
            }
            HelpCenter.onPopularTopicsClick = onPopularTopicsClick;
            /**
             * @description Function to handle analytics data on Help Center Contact Support click.
             * */
            function onContactSupportClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.contactLinkClick;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.contactUs,
                        event_label: _label
                    };
                    var _platformAnalyticsData = {
                        data_ga_label: _label,
                    };
                    GSS.analytics.event(_action, _data, _platformAnalyticsData, GSS.EventTypes.ButtonClick);
                }
            }
            HelpCenter.onContactSupportClick = onContactSupportClick;
            /**
             * @description Function to handle analytics data on Help Center Contact Us email click.
             * @param {boolean} isAuthenticated Indicates whether the user is authenticated.
             * */
            function onContactUsEmailClick(isAuthenticated) {
                var _label = document.getElementById('emailLink').getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.modalOpen;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.contactUs,
                        event_label: _label,
                        value: isAuthenticated ? 1 : 0
                    };
                    var _platformAnalyticsData = {
                        data_ga_label: _label,
                    };
                    GSS.analytics.event(_action, _data, _platformAnalyticsData, GSS.EventTypes.ButtonClick);
                }
            }
            HelpCenter.onContactUsEmailClick = onContactUsEmailClick;
            /**
             * @description Function to handle analytics data on Help Center Contact Us email submit button click.
             * */
            function onContactUsSubmitFormButtonClick() {
                var _label = document.getElementById('contactUs').getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.formSubmit;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.contactUs,
                        event_label: _label
                    };
                    GSS.analytics.event(_action, _data);
                }
            }
            HelpCenter.onContactUsSubmitFormButtonClick = onContactUsSubmitFormButtonClick;
            /**
             * @description Function to handle analytics data on Help Center Contact Us phone click.
             * */
            function onContactUsPhoneClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.phoneLinkClick;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.contactUs,
                        event_label: _label
                    };
                    var _platformAnalyticsData = {
                        data_ga_label: _label,
                    };
                    GSS.analytics.event(_action, _data, _platformAnalyticsData, GSS.EventTypes.ButtonClick);
                }
            }
            HelpCenter.onContactUsPhoneClick = onContactUsPhoneClick;
            /**
             * @description Function to handle analytics data on Help Center Contact Us phone number click.
             * */
            function onContactUsPhoneNumberClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.linkClick;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.contactUs,
                        event_label: _label
                    };
                    GSS.analytics.event(_action, _data);
                }
            }
            HelpCenter.onContactUsPhoneNumberClick = onContactUsPhoneNumberClick;
            /**
             * @description Function to handle analytics data on Help Center chat tile click.
             * */
            function onContactUsChatClick() {
                var _label = this.getAttribute(GSS.analytics.attributeNames.label);
                if (_label && _label !== '') {
                    var _action = GSS.analytics.eventActions.linkClick;
                    var _data = {
                        event_category: GSS.analytics.eventCategories.contactUs,
                        event_label: _label
                    };
                    var _platformAnalyticsData = {
                        data_ga_label: _label,
                    };
                    GSS.analytics.event(_action, _data, _platformAnalyticsData, GSS.EventTypes.ButtonClick);
                }
            }
            HelpCenter.onContactUsChatClick = onContactUsChatClick;
            /**
             * @description Function to handle analytics data on Help Center search.
             * */
            function onSearch(searchText) {
                var _action = GSS.analytics.eventActions.searchClick;
                var _data = {
                    event_category: GSS.analytics.eventCategories.search,
                    event_label: GSS.analytics.eventLabels.search,
                };
                var _platformAnalyticsData = {
                    data_ga_label: GSS.analytics.eventActions.search,
                    searchText: searchText,
                };
                GSS.analytics.event(_action, _data, _platformAnalyticsData, GSS.EventTypes.Search);
            }
            HelpCenter.onSearch = onSearch;
        })(HelpCenter = analytics.HelpCenter || (analytics.HelpCenter = {}));
    })(analytics = GSS.analytics || (GSS.analytics = {}));
})(GSS || (GSS = {}));
if (GSS.widgetName !== undefined) {
    GSS.tagging.tag(GSS.widgetName);
}
if (GSS.appName !== undefined) {
    GSS.tagging.tag(GSS.appName);
}
if (GSS.componentCodename !== undefined) {
    GSS.tagging.tag(GSS.componentCodename);
}
if (GSS.controllerAction !== undefined) {
    GSS.tagging.tag(GSS.componentCodename);
}
$(document).on('click', '#btc-sidenav', GSS.analytics.onPortalMenuClick);
$(document).on('click', '.c-sidenav__link', GSS.analytics.onPortalMenuItemClick);
$(document).on('click', 'a[data-nav-event]', GSS.analytics.onPortalMenuItemClick);
$(document).on('click', '#navbarbrand', GSS.analytics.onGallupAccessLogoClick);
$(document).on('click', '.language-selection-dropdown-menu-item', GSS.analytics.onLanguageSelectionClick);
$(document).on('click', '#bellNotificationClick', GSS.analytics.onNotificationBellClick);
$(document).on('click', 'a[data-gss-name="cartNav"]', GSS.analytics.onShoppingCartClick);
$(document).on('click', 'a[data-userlogin-anchor=""]', GSS.analytics.onMyAccountMenuClick);
$(document).on('click', '.account-menu-item', GSS.analytics.onMyAccountMenuItemClick);
$(document).on('click', '.survey-tile-button', GSS.analytics.onTopActionSurveyInviteClick);
$(document).on('click', '[' + GSS.analytics.attributeNames.createSurveyAction + '=' + GSS.analytics.attributeValues.createNewProjectModal + ']', GSS.analytics.onYourSurveysButtonClick);
$(document).on('click', 'a[href*="store.gallup.com"]', GSS.analytics.onMoreFromGallupAdsClick);
$(document).on('click', 'a[href*="gallup.com/learning/"]', GSS.analytics.onMoreFromGallupAdsCoursesClick);
$(document).on('click', 'a[data-ga-mobile-app-ad-anchor]', GSS.analytics.onMobileAppAdClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.linkAction, GSS.analytics.attributeValues.footerLinkClick), GSS.analytics.onFooterLinkClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterWatchButtonClick), GSS.analytics.HelpCenter.onWatchNowButtonClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterCategoryClick), GSS.analytics.HelpCenter.onCategoryClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterSubcategoryClick), GSS.analytics.HelpCenter.onSubCategoryClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterPopularTopicsClick), GSS.analytics.HelpCenter.onPopularTopicsClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterLinkClick), GSS.analytics.HelpCenter.onLinkClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterContactSupportLinkClick), GSS.analytics.HelpCenter.onContactSupportClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterCallSupportLinkClick), GSS.analytics.HelpCenter.onContactUsPhoneClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterPhoneNumberClick), GSS.analytics.HelpCenter.onContactUsPhoneNumberClick);
$(document).on('click', GSS.data.constructDataAtt(GSS.analytics.attributeNames.event, GSS.analytics.attributeValues.helpCenterChatTileClick), GSS.analytics.HelpCenter.onContactUsChatClick);
//# sourceMappingURL=GSS.portal.tagging.js.map